import React from 'react';
import { cssMap } from '@compiled/react';

import Heading from '@atlaskit/heading';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SnippetPreview } from '../preview/SnippetPreview.tsx';
import type { Snippet } from '../snippetTypes.ts';

const PREVIEW_WIDTH_VW = '40vw';
const PREVIEW_MAX_WIDTH_PX = 1000;
const PREVIEW_MIN_WIDTH_PX = 480;
const PREVIEW_MAX_HEIGHT_VW = '60vh';
const PREVIEW_PADDING = token('space.200');

const styles = cssMap({
	container: {
		width: PREVIEW_WIDTH_VW,
		maxWidth: `${PREVIEW_MAX_WIDTH_PX}px`,
		minWidth: `${PREVIEW_MIN_WIDTH_PX}px`,
		padding: PREVIEW_PADDING,

		maxHeight: PREVIEW_MAX_HEIGHT_VW,
		minHeight: '200px',

		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),

		display: 'flex',
		flexDirection: 'column',

		// Panel Width (400) + DISTANCE_FROM_ITEM (32) + PREVIEW_MIN_WIDTH_PX (480) = 812px
		'@media screen and (max-width: 812px)': {
			display: 'none',
		},
	},
	header: {
		paddingBottom: token('space.150'),
		paddingInline: token('space.050'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	previewWrapper: {
		flexGrow: 1,
		flexShrink: 1,
		overflow: 'hidden',
		height: 'calc(100% - 85px)',
	},
});

const SnippetInfo = ({ snippet }: { snippet: Snippet }) => (
	<div data-testid="snippet-info" css={styles.header}>
		<Stack space="space.100">
			<Heading size="small">{snippet.name}</Heading>
			{snippet.description && (
				<Text color="color.text.subtle" size="small">
					{snippet.description}
				</Text>
			)}
		</Stack>
	</div>
);

export const SnippetPopupPreviewFrame = ({ snippet }: { snippet?: Snippet }) => {
	const previewWrapperWidth = `min(${PREVIEW_WIDTH_VW}, ${PREVIEW_MAX_WIDTH_PX}px)`;
	const previewWidth = `calc(${previewWrapperWidth} - (${PREVIEW_PADDING} * 2))`;
	return (
		// NOTE: Cannot use Box or other primitives due to the usage of @media in the styles
		<div css={styles.container}>
			{snippet && (
				<>
					<SnippetInfo snippet={snippet} />
					<div css={styles.previewWrapper}>
						<SnippetPreview
							snippet={snippet}
							dimensions={{
								width: previewWidth,
								maxHeight: PREVIEW_MAX_HEIGHT_VW,
							}}
							renderWidth={800}
						/>
					</div>
				</>
			)}
		</div>
	);
};
